/**
 * The airship's procedural surface detail (ADR 0033, lifted from brad-airship
 * `airship-surfaces.js`): each texture is drawn once on a small canvas when a
 * ship scene is built (nothing is shipped as a file) and shared by every
 * material of that scene. Values are a near-white modulation (linear, no
 * colour-space decode), so a material's colour keeps owning the palette while
 * the texture adds seams, grain and wear. Magnification is nearest-neighbour
 * for the crisp HD-2D texel; minification keeps mipmaps against shimmer.
 *
 * - `deck`, `hull`, `paint`: long boards with staggered butt joints, caulked
 *   seams and treenails; `timber`, `iron`, `grass`: mottled; `rock`: broken
 *   strata; `canvas`: sailcloth.
 * - `sail(emblem)`: a whole sail (UV 0..1): narrow cloths, two reef bands
 *   with reef points, corner patches and, on the mainsail, Brad's "W" badge
 *   (drawn as a shape: no text is painted, ADR 0005).
 * - `ratline()`: three shrouds and 32 tarred ratlines on one see-through card.
 * - `grime(marks)`: the hull's weathering, painted once in hull space
 *   (`grimeFrame`): drips under the lights and scuppers, rust under the
 *   chainplates, aether bloom round the lift cradles, soot toward the bow.
 */
import * as THREE from 'three';
import { grimeFrame } from './hooks.ts';

export type GrimeMarks = { readonly windows: readonly number[]; readonly chainplates: readonly number[]; readonly cradles: readonly number[]; readonly scuppers: readonly number[] };

export type SurfaceTextures = {
  readonly deck: THREE.Texture;
  readonly hull: THREE.Texture;
  readonly paint: THREE.Texture;
  readonly timber: THREE.Texture;
  readonly iron: THREE.Texture;
  readonly canvas: THREE.Texture;
  readonly rock: THREE.Texture;
  readonly grass: THREE.Texture;
  sail(emblem: boolean): THREE.Texture;
  ratline(): THREE.Texture;
  grime(marks: GrimeMarks): THREE.Texture;
  dispose(): void;
};

type Draw = (g: CanvasRenderingContext2D, n: number) => void;

const rng = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
};

const context = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const g = canvas.getContext('2d');
  if (g === null) throw new Error('ship surfaces: no 2D canvas');
  return g;
};

const canvasTexture = (size: number, draw: Draw, repeat = true): THREE.CanvasTexture => {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  draw(context(c), size);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.wrapS = repeat ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
  t.wrapT = t.wrapS;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.anisotropy = 2;
  t.generateMipmaps = true;
  return t;
};

const grey = (v: number, a = 1): string => `rgba(${v},${v},${v},${a})`;
const tone = (r: number, g: number, b: number, a = 1): string => `rgba(${r | 0},${g | 0},${b | 0},${a})`;

type BoardOptions = { readonly size?: number; readonly rows?: number; readonly seam?: number; readonly butts?: number; readonly grain?: number; readonly warm?: number; readonly nails?: boolean; readonly wear?: number };

/** Long boards along +u with staggered butt joints, caulked seams and treenails; `rows` boards across the tile. */
const boards = (seed: number, { size = 256, rows = 16, seam = 0.42, butts = 2, grain = 0.1, warm = 1, nails = true, wear = 0.18 }: BoardOptions): THREE.CanvasTexture =>
  canvasTexture(size, (g, n) => {
    const r = rng(seed);
    const h = n / rows;
    for (let row = 0; row < rows; row += 1) {
      const y0 = Math.round(row * h);
      const y1 = Math.round((row + 1) * h);
      // Butt joints: `butts` per tile, offset per row, never aligned with neighbours.
      const cuts: number[] = [];
      const phase = ((row * 0.37) % 1) * n;
      for (let k = 0; k < butts; k += 1) cuts.push(Math.round((phase + (k * n) / butts + r() * 0.15 * n) % n));
      cuts.sort((a, b) => a - b);
      const first = cuts[0] ?? 0;
      const segs = cuts.map((c, k): readonly [number, number] => [c, k + 1 < cuts.length ? (cuts[k + 1] ?? c) : first + n]);
      for (const [a, b] of segs) {
        const base = 214 + (r() - 0.5) * 46;
        const rr = base * (1 + 0.035 * warm);
        const gg = base * (1 - 0.01 * warm);
        const bb = base * (1 - 0.06 * warm);
        for (const off of [0, -n]) {
          g.fillStyle = tone(rr, gg, bb);
          g.fillRect(a + off, y0, b - a, y1 - y0);
          // Grain: thin streaks along the board.
          for (let s = 0; s < (y1 - y0) * 1.4; s += 1) {
            const y = y0 + 1 + r() * (y1 - y0 - 2);
            const x = a + off + r() * (b - a);
            const len = (0.2 + r() * 0.6) * (b - a);
            g.fillStyle = r() < 0.5 ? grey(0, grain * (0.4 + r())) : grey(255, grain * 0.5 * r());
            g.fillRect(x, Math.round(y), len, 1);
          }
          // Occasional knot.
          if (r() < 0.25) {
            const kx = a + off + r() * (b - a);
            const ky = y0 + (y1 - y0) / 2;
            g.fillStyle = grey(0, 0.28);
            g.fillRect(kx - 1, ky - 1, 3, 2);
          }
          // Butt joint seam and treenails either side.
          g.fillStyle = grey(0, seam);
          g.fillRect(a + off, y0, 1, y1 - y0);
          if (nails) {
            for (const nx of [a + off + 3, b + off - 4]) {
              for (const ny of [y0 + 2, y1 - 3]) {
                g.fillStyle = grey(0, 0.35);
                g.fillRect(nx, ny, 1, 1);
              }
            }
          }
        }
      }
      // Caulked seam along the board edge, with a faint lit lip under it.
      g.fillStyle = grey(0, seam);
      g.fillRect(0, y0, n, 1);
      g.fillStyle = grey(255, 0.06);
      g.fillRect(0, y0 + 1, n, 1);
    }
    // Broad soft wear / weathering blotches.
    for (let i = 0; i < 26; i += 1) {
      const x = r() * n;
      const y = r() * n;
      const rad = (0.05 + r() * 0.14) * n;
      const grd = g.createRadialGradient(x, y, 0, x, y, rad);
      const light = r() < 0.5;
      grd.addColorStop(0, light ? grey(255, wear * 0.5) : grey(0, wear * 0.6));
      grd.addColorStop(1, grey(light ? 255 : 0, 0));
      g.fillStyle = grd;
      for (const [dx, dy] of [
        [0, 0],
        [n, 0],
        [-n, 0],
        [0, n],
        [0, -n],
      ] as const)
        g.fillRect(x - rad + dx, y - rad + dy, rad * 2, rad * 2);
    }
  });

type MottleOptions = { readonly size?: number; readonly spots?: number; readonly dark?: number; readonly light?: number; readonly base?: number; readonly streaks?: number };

const mottled = (seed: number, { size = 128, spots = 180, dark = 0.16, light = 0.08, base = 222, streaks = 0 }: MottleOptions): THREE.CanvasTexture =>
  canvasTexture(size, (g, n) => {
    const r = rng(seed);
    g.fillStyle = grey(base);
    g.fillRect(0, 0, n, n);
    for (let i = 0; i < spots; i += 1) {
      const x = r() * n;
      const y = r() * n;
      const s = 1 + r() * 3;
      g.fillStyle = r() < 0.6 ? grey(0, dark * r()) : grey(255, light * r());
      g.fillRect(Math.round(x), Math.round(y), Math.round(s), Math.round(s));
    }
    for (let i = 0; i < streaks; i += 1) {
      const x = r() * n;
      const y = r() * n;
      g.fillStyle = grey(0, 0.05 + r() * 0.07);
      g.fillRect(Math.round(x), Math.round(y), 1, Math.round(4 + r() * n * 0.3));
    }
  });

/** Sedimentary rock: broken horizontal strata with vertical cracks and grit. */
const strata = (seed: number): THREE.CanvasTexture =>
  canvasTexture(128, (g, n) => {
    const r = rng(seed);
    g.fillStyle = grey(220);
    g.fillRect(0, 0, n, n);
    let y = 0;
    while (y < n) {
      const h = 4 + Math.round(r() * 12);
      g.fillStyle = r() < 0.5 ? grey(0, 0.04 + r() * 0.1) : grey(255, 0.05 + r() * 0.06);
      g.fillRect(0, y, n, h);
      g.fillStyle = grey(0, 0.28);
      for (let x = 0; x < n; x += 2) if (r() < 0.8) g.fillRect(x, y + Math.round(Math.sin(x * 0.2 + y) * 1.2), 2, 1);
      for (let k = 0; k < 3; k += 1) {
        g.fillStyle = grey(0, 0.22);
        g.fillRect(Math.round(r() * n), y, 1, h);
      }
      y += h;
    }
    for (let i = 0; i < 500; i += 1) {
      g.fillStyle = r() < 0.5 ? grey(0, 0.15) : grey(255, 0.1);
      g.fillRect(Math.round(r() * n), Math.round(r() * n), 1, 1);
    }
  });

/** Sailcloth: vertical cloths (about 0.6 m) sewn with doubled seams, a faint weave and weather staining low on the sail. */
const canvasCloth = (seed: number): THREE.CanvasTexture =>
  canvasTexture(256, (g, n) => {
    const r = rng(seed);
    g.fillStyle = grey(236);
    g.fillRect(0, 0, n, n);
    for (let i = 0; i < 1400; i += 1) {
      g.fillStyle = r() < 0.5 ? grey(0, 0.05) : grey(255, 0.08);
      g.fillRect(Math.round(r() * n), Math.round(r() * n), 2, 1);
    }
    const cloth = n / 6;
    for (let k = 0; k < 6; k += 1) {
      const x = Math.round(k * cloth);
      g.fillStyle = grey(0, 0.2);
      g.fillRect(x, 0, 1, n);
      g.fillStyle = grey(0, 0.1);
      g.fillRect(x + 3, 0, 1, n);
      g.fillStyle = grey(255, 0.12);
      g.fillRect(x + 1, 0, 2, n);
      // Slight per-cloth tone: bolts of canvas never match exactly.
      g.fillStyle = r() < 0.5 ? grey(0, 0.03 + r() * 0.04) : grey(255, 0.04 * r());
      g.fillRect(x + 4, 0, cloth - 4, n);
    }
    for (let i = 0; i < 10; i += 1) {
      const x = r() * n;
      const y = n * (0.4 + r() * 0.6);
      const rad = n * (0.05 + r() * 0.12);
      const grd = g.createRadialGradient(x, y, 0, x, y, rad);
      grd.addColorStop(0, tone(90, 70, 40, 0.07));
      grd.addColorStop(1, tone(90, 70, 40, 0));
      g.fillStyle = grd;
      g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
    }
  });

/**
 * Brad's "W" badge as a shape (the prototype set the letter in a serif face; a painted glyph here, so no font is
 * read and no text is painted): two V strokes with flared serifs, centred on (0, 0), `s` the badge's side.
 */
const badgeW = (g: CanvasRenderingContext2D, s: number): void => {
  const h = s * 0.62;
  const top = -h / 2;
  const bottom = h / 2;
  const w = s * 0.39;
  const stroke = s * 0.11;
  const thin = s * 0.05;
  g.beginPath();
  // Outer edge, left to right along the top, then the four diagonals.
  g.moveTo(-w - stroke * 0.6, top);
  g.lineTo(-w + stroke * 1.0, top);
  g.lineTo(-w * 0.45 + thin * 0.5, bottom - s * 0.2);
  g.lineTo(-thin * 0.6, top + s * 0.06);
  g.lineTo(thin * 0.6, top + s * 0.06);
  g.lineTo(w * 0.45 - thin * 0.5, bottom - s * 0.2);
  g.lineTo(w - stroke * 1.0, top);
  g.lineTo(w + stroke * 0.6, top);
  g.lineTo(w * 0.42 + thin, bottom);
  g.lineTo(w * 0.42 - stroke * 0.8, bottom);
  g.lineTo(0, top + s * 0.2);
  g.lineTo(-w * 0.42 + stroke * 0.8, bottom);
  g.lineTo(-w * 0.42 - thin, bottom);
  g.closePath();
  g.fill();
};

/** A whole sail (UV 0..1 over the sail): narrow cloths parallel to the leech, two reef bands, corner patches, and on the mainsail the badge. */
const sailTexture = (emblem: boolean): THREE.CanvasTexture => {
  const t = canvasTexture(512, (g, n) => {
    const r = rng(emblem ? 71 : 73);
    g.fillStyle = grey(236);
    g.fillRect(0, 0, n, n);
    for (let i = 0; i < 5000; i += 1) {
      g.fillStyle = r() < 0.5 ? grey(0, 0.045) : grey(255, 0.07);
      g.fillRect(Math.round(r() * n), Math.round(r() * n), 2, 1);
    }
    const cloths = emblem ? 16 : 12;
    const w = n / cloths;
    for (let k = 0; k < cloths; k += 1) {
      const x = Math.round(k * w);
      g.fillStyle = r() < 0.5 ? grey(0, 0.02 + r() * 0.05) : grey(255, 0.05 * r());
      g.fillRect(x, 0, Math.ceil(w), n);
      g.fillStyle = grey(0, 0.22);
      g.fillRect(x, 0, 1, n);
      g.fillStyle = grey(0, 0.1);
      g.fillRect(x + 3, 0, 1, n);
    }
    // Reef bands (texture v grows upward: canvas y = n at the foot).
    for (const f of [0.14, 0.27]) {
      const y = Math.round(n * (1 - f));
      g.fillStyle = grey(0, 0.12);
      g.fillRect(0, y - 3, n, 7);
      g.fillStyle = grey(255, 0.12);
      g.fillRect(0, y - 2, n, 2);
      for (let x = w / 2; x < n; x += w) {
        g.fillStyle = grey(40, 0.7);
        g.fillRect(Math.round(x), y - 1, 2, 9);
      }
    }
    // Tabling (doubled hems) and reinforcing patches at the corners.
    g.fillStyle = grey(0, 0.14);
    for (const [x, y, ww, hh] of [
      [0, 0, n, 9],
      [0, n - 9, n, 9],
      [0, 0, 9, n],
      [n - 9, 0, 9, n],
    ] as const)
      g.fillRect(x, y, ww, hh);
    for (const [cx, cy] of [
      [0, 0],
      [n, 0],
      [0, n],
      [n, n],
    ] as const) {
      g.fillStyle = grey(0, 0.1);
      g.beginPath();
      g.moveTo(cx, cy);
      g.lineTo(cx + (cx !== 0 ? -1 : 1) * n * 0.16, cy);
      g.lineTo(cx, cy + (cy !== 0 ? -1 : 1) * n * 0.16);
      g.fill();
    }
    if (emblem) {
      const s = n * 0.3;
      const x0 = n * 0.36;
      const y0 = n * 0.3;
      g.save();
      g.translate(x0 + s / 2, y0 + s / 2);
      g.rotate(-0.06);
      g.fillStyle = tone(190, 48, 36, 0.92);
      g.fillRect(-s / 2, -s / 2, s, s);
      // Weathered paint: flecks of canvas showing through.
      for (let i = 0; i < 160; i += 1) {
        g.fillStyle = grey(236, 0.35 + r() * 0.4);
        g.fillRect(-s / 2 + r() * s, -s / 2 + r() * s, 1 + r() * 2, 1);
      }
      g.fillStyle = tone(34, 24, 20, 0.95);
      badgeW(g, s);
      g.restore();
    }
  });
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  return t;
};

/** Ratline card: three shrouds (u = 0, .5, 1) with 32 tarred ratlines that sag between them, seized at every crossing. */
const ratlineTexture = (): THREE.CanvasTexture => {
  const W = 128;
  const H = 1024;
  const rungs = 32;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = context(c);
  const rope = (x0: number, y0: number, x1: number, y1: number, w: number, light: string): void => {
    g.strokeStyle = light;
    g.lineWidth = w;
    g.beginPath();
    g.moveTo(x0, y0);
    g.lineTo(x1, y1);
    g.stroke();
  };
  for (let k = 0; k < rungs; k += 1) {
    const y = H - (k + 0.5) * (H / rungs);
    for (const [a, b] of [
      [4, W / 2],
      [W / 2, W - 4],
    ] as const) {
      const sag = 3;
      const n = 8;
      for (let i = 0; i < n; i += 1) {
        const t0 = i / n;
        const t1 = (i + 1) / n;
        const yy0 = y + sag * 4 * t0 * (1 - t0);
        const yy1 = y + sag * 4 * t1 * (1 - t1);
        rope(a + (b - a) * t0, yy0 + 1, a + (b - a) * t1, yy1 + 1, 2, 'rgba(40,30,20,1)');
        rope(a + (b - a) * t0, yy0, a + (b - a) * t1, yy1, 2, 'rgba(225,215,195,1)');
      }
    }
  }
  for (const x of [4, W / 2, W - 4]) {
    g.fillStyle = 'rgba(45,35,25,1)';
    g.fillRect(x - 3, 0, 6, H);
    g.fillStyle = 'rgba(200,190,170,1)';
    g.fillRect(x - 2, 0, 2, H);
    for (let k = 0; k < rungs; k += 1) {
      g.fillStyle = 'rgba(30,22,16,1)';
      g.fillRect(x - 4, H - (k + 0.5) * (H / rungs) - 2, 8, 5);
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.RepeatWrapping;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.anisotropy = 4;
  return t;
};

/** The hull's weathering in hull space (world x along the ship, y up): every mark has a cause. */
const grimeTexture = ({ windows, chainplates, cradles, scuppers }: GrimeMarks): THREE.CanvasTexture => {
  const W = 1024;
  const H = 256;
  const f = grimeFrame;
  const r = rng(907);
  const X = (x: number): number => ((x - f.x0) / (f.x1 - f.x0)) * W;
  const Y = (y: number): number => (1 - (y - f.y0) / (f.y1 - f.y0)) * H;
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const g = context(c);
  g.fillStyle = grey(255);
  g.fillRect(0, 0, W, H);
  const keel = g.createLinearGradient(0, Y(-2.2), 0, Y(-6.5));
  keel.addColorStop(0, 'rgba(60,45,30,0)');
  keel.addColorStop(1, 'rgba(60,45,30,.55)');
  g.fillStyle = keel;
  g.fillRect(0, Y(-2.2), W, H);
  const streak = (x: number, yTop: number, len: number, width: number, rgba: string, count = 3): void => {
    for (let k = 0; k < count; k += 1) {
      const xx = X(x) + (r() - 0.5) * width * 18;
      const l = len * (0.5 + r() * 0.6);
      const grd = g.createLinearGradient(0, Y(yTop), 0, Y(yTop - l));
      grd.addColorStop(0, rgba);
      grd.addColorStop(1, rgba.replace(/[\d.]+\)$/, '0)'));
      g.fillStyle = grd;
      g.fillRect(Math.round(xx), Y(yTop), 2 + Math.round(r() * 3), Y(yTop - l) - Y(yTop));
    }
  };
  for (const x of windows) {
    streak(x - 0.25, -1.85, 1.6, 1, 'rgba(45,35,25,.6)');
    streak(x + 0.25, -1.85, 1.2, 1, 'rgba(45,35,25,.5)', 2);
  }
  for (const x of scuppers) streak(x, -0.05, 2.4, 0.6, 'rgba(40,32,25,.62)', 4);
  for (const x of chainplates) streak(x, -0.75, 1.8, 0.5, 'rgba(125,50,20,.7)', 3);
  for (const x of cradles) {
    for (const [dy, a] of [
      [-1.7, 0.22],
      [-5.0, 0.18],
    ] as const) {
      const grd = g.createRadialGradient(X(x), Y(dy), 0, X(x), Y(dy), 40);
      grd.addColorStop(0, `rgba(70,110,125,${a})`);
      grd.addColorStop(1, 'rgba(70,110,125,0)');
      g.fillStyle = grd;
      g.fillRect(X(x) - 40, Y(dy) - 40, 80, 80);
    }
    streak(x, -2.0, 1.2, 1.5, 'rgba(70,110,125,.3)', 3);
  }
  // Bow: spray and soot from the galley stove forward of the stem.
  const bow = g.createLinearGradient(X(56), 0, X(63.5), 0);
  bow.addColorStop(0, 'rgba(40,35,30,0)');
  bow.addColorStop(1, 'rgba(40,35,30,.35)');
  g.fillStyle = bow;
  g.fillRect(X(56), 0, W, H);
  // Scuffs along the wales and blotchy weathering everywhere.
  for (let i = 0; i < 260; i += 1) {
    const x = r() * W;
    const y = r() * H;
    const w = 2 + r() * 14;
    g.fillStyle = r() < 0.55 ? `rgba(40,32,25,${0.06 + r() * 0.1})` : `rgba(255,250,235,${0.05 + r() * 0.08})`;
    g.fillRect(Math.round(x), Math.round(y), Math.round(w), 1 + Math.round(r()));
  }
  for (const y of [-0.62, -2.56]) {
    for (let i = 0; i < 40; i += 1) {
      g.fillStyle = `rgba(255,245,225,${0.12 + r() * 0.15})`;
      g.fillRect(Math.round(r() * W), Math.round(Y(y) - 1 + r() * 2), Math.round(3 + r() * 10), 1);
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.NoColorSpace;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  return t;
};

/** The surface set of one ship scene (`surfaceTextures`); the sails, the ratline card and the grime map are made on first use. */
export const createSurfaceTextures = (): SurfaceTextures => {
  const made: THREE.Texture[] = [];
  const keep = <T extends THREE.Texture>(t: T): T => {
    made.push(t);
    return t;
  };
  const sails = new Map<boolean, THREE.Texture>();
  let ratline: THREE.Texture | null = null;
  let grime: THREE.Texture | null = null;
  return {
    deck: keep(boards(11, { size: 256, rows: 22, seam: 0.5, butts: 2, grain: 0.09, wear: 0.2 })),
    hull: keep(boards(23, { size: 256, rows: 16, seam: 0.55, butts: 1, grain: 0.12, wear: 0.26, nails: true })),
    paint: keep(boards(37, { size: 128, rows: 8, seam: 0.3, butts: 1, grain: 0.04, warm: 0, wear: 0.12, nails: false })),
    timber: keep(mottled(41, { size: 128, spots: 320, dark: 0.22, light: 0.06, base: 214, streaks: 60 })),
    iron: keep(mottled(53, { size: 64, spots: 140, dark: 0.3, light: 0.12, base: 200 })),
    canvas: keep(canvasCloth(61)),
    rock: keep(strata(83)),
    grass: keep(mottled(97, { size: 128, spots: 900, dark: 0.3, light: 0.18, base: 206 })),
    sail(emblem) {
      let t = sails.get(emblem);
      if (t === undefined) {
        t = keep(sailTexture(emblem));
        sails.set(emblem, t);
      }
      return t;
    },
    ratline() {
      ratline ??= keep(ratlineTexture());
      return ratline;
    },
    grime(marks) {
      grime ??= keep(grimeTexture(marks));
      return grime;
    },
    dispose() {
      for (const t of made) t.dispose();
    },
  };
};
